import { Col, Row } from "antd";
import classNames from "classnames";
import cuid from "cuid";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  BANK_ACCOUNT_OWNER_TYPE,
  DEBIT_POSSIBLE_DATES,
  PAYMENT_FREQUENCIES,
} from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
  setDefaultIfEmpty,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Actions from "../Components/Actions";
import Button from "../Components/Button";
import Card from "../Components/Card";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

function View({
  initialValues,
  subscription,
  project,
  onNext,
  onBack,
  loading,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: initialValues,
    onSubmit: onNext,
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  const PAYMENT_PAYER = {
    [true]: () => {
      if (subscription?.locked) return;
      const owner = {
        id: get(values, "payment.payer.owner.id", cuid()),
        firstName: "",
        lastName: "",
        isAdherent: false,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      };
      setFieldValue("payment.payer.owner", owner);
      if (get(values, "isPayerReceiver") === true) {
        setFieldValue("payment.receiver", {
          ...get(values, "payment.payer"),
          owner,
        });
      }
    },
    [false]: () => {
      if (subscription?.locked) return;
      const owner = {
        id: get(values, "payment.payer.owner.id", cuid()),
        firstName: get(project, "contact.user.firstname"),
        lastName: get(project, "contact.user.lastname"),
        isAdherent: true,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      };
      setFieldValue("payment.payer.owner", owner);
      if (get(values, "isPayerReceiver") === true) {
        setFieldValue("payment.receiver", {
          ...get(values, "payment.payer"),
          owner,
        });
      }
    },
  };

  useEffect(() => {
    if (get(initialValues, "payment.payer.owner.isAdherent")) {
      PAYMENT_PAYER[false]();
    }
  }, []);

  return (
    <Layout>
      <Header
        title="Renseignez vos informations bancaires"
        highlight="informations bancaires"
        subtitle="Vos informations bancaires sont protégées et nécessaires pour gérer vos cotisations"
        onBack={onBack}
      />
      <Row justify="center">
        <Col xs={24} md={18}>
          <div className="grid--1">
            <Input
              type="checkbox"
              label={
                "Cochez cette case si la cotisation sera payée par quelqu'un d’autre"
              }
              checked={!get(values, "payment.payer.owner.isAdherent")}
              onChange={({ target: { checked } }) => {
                PAYMENT_PAYER[checked](checked);
              }}
              disabled={subscription.locked}
            />
            <div className="grid--2">
              <Input
                placeholder={"ex : Doe"}
                label={"Nom titulaire du compte"}
                {...bindWebTextFieldInputProps({
                  name: "payment.payer.owner.lastName",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
              />
              <Input
                placeholder={"ex : John"}
                label={"Prénom titulaire du compte"}
                {...bindWebTextFieldInputProps({
                  name: "payment.payer.owner.firstName",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
              />
            </div>

            <div className="grid--2">
              <Input
                placeholder={"ex : FR76 3000 4001 2600 0100 6123 456"}
                label={"IBAN"}
                trim
                {...bindInputProps({
                  name: "payment.payer.IBAN",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={"ex : PRNSFRP1"}
                label={"BIC"}
                trim
                {...bindInputProps({
                  name: "payment.payer.BIC",
                  values,
                  ...formProps,
                })}
              />
            </div>

            <div className="grid--2">
              <Select
                options={setDefaultIfEmpty(
                  get(subscription, "insurancePlan.fields.payment.frequency"),
                  Object.values(PAYMENT_FREQUENCIES),
                ).map((value) => ({
                  value,
                  label: TRANSLATION.PAYMENT_FREQUENCY.OPTIONS[value],
                }))}
                label={TRANSLATION.PAYMENT_FREQUENCY.LABEL}
                placeholder={"Sélectionnez ..."}
                {...bindInputProps({
                  name: "payment.frequency",
                  values,
                  ...formProps,
                })}
                disabled={subscription?.locked}
              />
              <Select
                options={setDefaultIfEmpty(
                  get(subscription, "insurancePlan.fields.payment.debitDay"),
                  DEBIT_POSSIBLE_DATES,
                ).map((value) => ({
                  value,
                  label: value,
                }))}
                label={TRANSLATION.DEBIT_DAY.LABEL}
                placeholder={"Sélectionnez ..."}
                {...bindInputProps({
                  name: "payment.debitDay",
                  values,
                  ...formProps,
                })}
                disabled={true}
                // disabled={subscription?.locked}
              />
            </div>

            <Actions
              label="Le compte de prélèvement des cotisations sera-t-il le même que le compte de remboursement des prestations ?"
              actions={
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      if (subscription?.locked) return;
                      const payer = get(values, "payment.payer");
                      const isAdherent = get(payer, "owner.isAdherent", false);

                      setFieldValue("isPayerReceiver", true);
                      setFieldValue("payment.receiver", {
                        ...payer,
                        owner: {
                          ...payer.owner,
                          isAdherent: isAdherent,
                        },
                      });
                    }}
                    type={classNames({
                      primary: get(values, "isPayerReceiver") === true,
                      white: get(values, "isPayerReceiver", false) === false,
                    })}
                  >
                    Oui
                  </Button>

                  <Button
                    size="small"
                    onClick={() => {
                      if (subscription?.locked) return;

                      const payerFirstName = get(
                        values,
                        "payment.payer.owner.firstName",
                        "",
                      );
                      const payerLastName = get(
                        values,
                        "payment.payer.owner.lastName",
                        "",
                      );
                      const payerIBAN = get(values, "payment.payer.IBAN", "");
                      const payerBIC = get(values, "payment.payer.BIC", "");
                      const isAdherent = get(
                        values,
                        "payment.payer.owner.isAdherent",
                        false,
                      );

                      setFieldValue("isPayerReceiver", false);
                      setFieldValue("payment.receiver", {
                        id: cuid(),
                        IBAN: payerIBAN,
                        BIC: payerBIC,
                        owner: {
                          id: cuid(),
                          firstName: payerFirstName,
                          lastName: payerLastName,
                          isAdherent: isAdherent,
                          type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
                        },
                      });
                    }}
                    type={classNames({
                      primary: get(values, "isPayerReceiver") === false,
                      white: get(values, "isPayerReceiver", true) === true,
                    })}
                  >
                    Non
                  </Button>
                </>
              }
              body={
                get(values, "isPayerReceiver") === false && (
                  <Card bg="white">
                    <div className="grid--2">
                      <Input
                        label="Nom titulaire du compte"
                        placeholder="ex : Doe"
                        {...bindWebTextFieldInputProps({
                          name: "payment.receiver.owner.lastName",
                          values,
                          setFieldValue,
                          ...formProps,
                        })}
                      />
                      <Input
                        label="Prénom titulaire du compte"
                        placeholder="ex : John"
                        {...bindWebTextFieldInputProps({
                          name: "payment.receiver.owner.firstName",
                          values,
                          setFieldValue,
                          ...formProps,
                        })}
                      />
                    </div>
                    <div className="grid--2">
                      <Input
                        label="IBAN"
                        placeholder="FR76 3000 4001 2600 0100 6123 456"
                        trim
                        {...bindInputProps({
                          name: "payment.receiver.IBAN",
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label="BIC"
                        placeholder="PRNSFRP1"
                        trim
                        {...bindInputProps({
                          name: "payment.receiver.BIC",
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                  </Card>
                )
              }
            />
          </div>
        </Col>
      </Row>
      <ActionButtons
        disabled={!isEmpty(formProps.errors)}
        onNext={handleSubmit}
        onBack={onBack}
        loading={loading}
      />
    </Layout>
  );
}

export default View;
